/**
 * AttendanceDayDrawer (ATT-DAILY-001)
 * Right-side session history drawer: full raw sessions for one employee's
 * local attendance day — login/logout in employee timezone, durations,
 * logout reasons, nested breaks (paid/unpaid), expandable audit info and
 * day totals. Read-only audit surface.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Loader2, Clock, Coffee, AlertTriangle, MonitorSmartphone, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Badge } from '../ui/badge';

const API = process.env.REACT_APP_BACKEND_URL || '';

const fmt = (m) => {
  if (m == null) return '—';
  const h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
};

const SessionCard = ({ s }) => {
  const [showAudit, setShowAudit] = useState(false);
  const active = s.status === 'Active';
  return (
    <div
      className={`rounded-xl border p-3 ${active ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-200 bg-white'}`}
      data-testid={`session-card-${s.index}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide">Session {s.index}</span>
        <span className="text-[13px] font-semibold text-slate-800 font-mono">
          {s.login_local} → {active ? 'Active' : s.logout_local}
        </span>
        <span className="text-[12px] text-slate-500">· {fmt(s.duration_minutes)}</span>
        <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-full ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
          {s.status}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-2 flex-wrap text-[11.5px] text-slate-500">
        {s.logout_reason_label && (
          <span className={s.is_abnormal_closure ? 'text-amber-700 font-semibold' : ''}>
            {s.is_abnormal_closure && <AlertTriangle className="w-3 h-3 inline mr-0.5 -mt-0.5" />}
            {s.logout_reason_label}
          </span>
        )}
        {s.is_short && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-100" title="Informational: session under 5 minutes">
            Short session
          </span>
        )}
      </div>
      {s.breaks?.length > 0 && (
        <div className="mt-2 space-y-1">
          {s.breaks.map((b, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[11.5px] text-slate-600 pl-2 border-l-2 border-slate-100" data-testid={`session-${s.index}-break-${i}`}>
              <Coffee className="w-3 h-3 text-slate-400 flex-shrink-0" />
              <span className="font-medium">{b.break_type || 'Break'}</span>
              <span className="font-mono text-slate-400">{b.start_local}{b.end_local ? ` → ${b.end_local}` : ' (open)'}</span>
              <span>· {fmt(b.duration_minutes)}</span>
              <span className={`text-[9.5px] font-bold px-1 rounded ${b.is_paid ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'}`}>
                {b.is_paid ? 'PAID' : 'UNPAID'}
              </span>
              {b.entitlement_waived && <span className="text-[9.5px] text-slate-400">(≤5m waived)</span>}
              {b.flagged && (
                <span className="text-[9.5px] text-amber-700 font-semibold" title={b.flagged_reason || 'Flagged'}>⚑ flagged</span>
              )}
            </div>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => setShowAudit((v) => !v)}
        className="mt-2 flex items-center gap-1 text-[10.5px] text-slate-400 hover:text-slate-600"
        data-testid={`session-${s.index}-audit-toggle`}
      >
        {showAudit ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <MonitorSmartphone className="w-3 h-3" /> Audit info
      </button>
      {showAudit && (
        <div className="mt-1 text-[10.5px] text-slate-500 font-mono bg-slate-50 rounded-lg p-2 grid grid-cols-2 gap-x-3 gap-y-0.5" data-testid={`session-${s.index}-audit`}>
          <span>Device: {s.audit?.device_type || '—'}</span>
          <span>Browser: {s.audit?.browser || '—'}</span>
          <span>OS: {s.audit?.os || '—'}</span>
          <span>IP: {s.audit?.ip_address || '—'}</span>
          <span className="col-span-2">Login UTC: {s.login_utc || '—'}</span>
          <span className="col-span-2">Logout UTC: {s.logout_utc || '—'}</span>
        </div>
      )}
    </div>
  );
};

export default function AttendanceDayDrawer({ target, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!target) { setData(null); return; }
    setLoading(true);
    axios.get(`${API}/api/reports/attendance/daily/sessions`, {
      params: { user_id: target.user_id, date: target.date },
    })
      .then((r) => setData(r.data))
      .catch(() => setData({ error: true }))
      .finally(() => setLoading(false));
  }, [target]);

  return (
    <Sheet open={!!target} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="attendance-day-drawer">
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-600" />
            {data?.employee?.user_name || target?.user_name || 'Session History'}
          </SheetTitle>
          <SheetDescription data-testid="drawer-subtitle">
            {target?.date} · {data?.employee?.role?.replace(/_/g, ' ') || ''} · {data?.timezone || ''}
          </SheetDescription>
        </SheetHeader>
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading sessions…
          </div>
        ) : data?.error ? (
          <p className="py-10 text-center text-sm text-slate-400">Could not load session history.</p>
        ) : data ? (
          <div className="mt-4 space-y-3">
            {data.flags?.needs_review && (
              <div className="flex items-center gap-2 text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="drawer-needs-review">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                Needs review: {[
                  data.flags.abnormal_closure && 'abnormal session closure',
                  data.flags.overlapping_sessions && 'overlapping sessions',
                  data.flags.flagged_breaks && 'flagged breaks',
                ].filter(Boolean).join(', ') || 'attendance anomaly'}
              </div>
            )}
            {data.sessions.map((s) => <SessionCard key={s.session_id || s.index} s={s} />)}
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-1" data-testid="drawer-totals">
              <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1">Day Totals</div>
              <div className="flex justify-between text-[12.5px]"><span className="text-slate-500">Total Session Time</span><span className="font-semibold font-mono">{data.totals.total_worked_hhmm}</span></div>
              <div className="flex justify-between text-[12.5px]"><span className="text-slate-500">Paid Breaks</span><span className="font-mono">{fmt(data.totals.break_paid_minutes)}</span></div>
              <div className="flex justify-between text-[12.5px]"><span className="text-slate-500">Unpaid Breaks</span><span className="font-mono text-rose-600">−{fmt(data.totals.break_unpaid_minutes)}</span></div>
              <div className="flex justify-between text-[13px] border-t border-slate-200 pt-1 mt-1"><span className="font-semibold text-slate-700">Net Payable</span><span className="font-bold font-mono text-emerald-700">{data.totals.net_payable_hhmm}</span></div>
              <div className="text-[10.5px] text-slate-400 pt-1">Timezone: {data.timezone} · {data.totals.session_count} session{data.totals.session_count === 1 ? '' : 's'} · matches Payroll Summary math</div>
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
